import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@vwfs-bronson/bronson-react';
import { Loyalty, getCertificateEndpoint } from '@cp-mx/common';
import { useAnalyticsActionTracker } from '../../../analytics';
import { Notification, NotificationStatus, Spinner } from '@cp-shared-19/frontend-ui';
import { CpDataApi } from '../../../cp-xhr';
import { useNotification } from '../useNotification';
import { useNotificationCenter } from '../../notification-center/useNotificationCenter';

export const LoyaltyNotificationUi: React.FC<{ loyalties?: Loyalty[] }> = ({ loyalties }) => {
    const { t } = useTranslation('loyalty-notification');
    const [isLoading, setIsLoading] = useState<number[]>([]);
    const [notificationError, setNotificationError] = useState<number[]>([]);
    const { notificationState, setNotification } = useNotification();
    const { data: notifications } = useNotificationCenter();
    const { onAction: onApiError } = useAnalyticsActionTracker('onRequestCertificateSubmitAPIError');
    const { onAction: onSuccess } = useAnalyticsActionTracker('onRequestCertificateSuccess');
    const handleOnClose = (notification: Loyalty) => {
        setNotification(notification.loyaltyProgramId);
    };

    const getFileCertificate = async (loyalty: Loyalty) => {
        setIsLoading(state => {
            return [...state, loyalty.loyaltyProgramId];
        });
        CpDataApi.get(getCertificateEndpoint(`${loyalty.loyaltyProgramId}?pdf=true`))
            .then(response => {
                const url = response.data.signedUrl;
                window.open(url);
                onSuccess();
                setIsLoading(state => {
                    return state.filter(e => e !== loyalty.loyaltyProgramId);
                });
            })
            .catch(() => {
                onApiError();
                setIsLoading(state => {
                    return state.filter(e => e !== loyalty.loyaltyProgramId);
                });
                setNotificationError(state => {
                    return [...state, loyalty.loyaltyProgramId];
                });
            });
    };

    const displayLoadingSpinner = (loyalty: Loyalty) => {
        const linkText = (
            <>
                {t('warning.moreInfo')}
                <Button
                    className="u-font-size-fs0"
                    style={{ textTransform: 'lowercase' }}
                    link
                    onClick={() => getFileCertificate(loyalty)}
                >
                    {t('warning.link')}
                </Button>
                {t('warning.certificate')}
            </>
        );

        const spinner = <Spinner small center />;

        if (isLoading.includes(loyalty.loyaltyProgramId)) {
            return spinner;
        } else {
            return linkText;
        }
    };

    if (!loyalties?.length) {
        return null;
    }

    const seenCertificates = new Set<string>();
    const loyaltiesToShow: Loyalty[] = loyalties.filter(loyalty => {
        if (seenCertificates.has(loyalty.certificate)) {
            return false;
        }
        const qualifies =
            loyalty.contractNumber === 'ALL' ||
            (notifications ?? []).some(n => n['loyalty'] && n.contractNumber === loyalty.contractNumber);
        if (qualifies) {
            seenCertificates.add(loyalty.certificate);
        }
        return qualifies;
    });

    return (
        <>
            {loyaltiesToShow.map(loyalty => {
                if (!notificationError.includes(loyalty.loyaltyProgramId)) {
                    if (!loyalty.status) {
                        return (
                            <Notification
                                showCloseButton
                                visible={!notificationState.includes(loyalty.loyaltyProgramId)}
                                onCloseClick={() => handleOnClose(loyalty)}
                                key={loyalty.loyaltyProgramId}
                                headline={t('warning.headline')}
                                testId={`testIdWarning${loyalty.loyaltyProgramId}`}
                            >
                                {t('warning.loyalty')}
                                {t('warning.benefit')}
                                <br />
                                <br />
                                {displayLoadingSpinner(loyalty)}
                            </Notification>
                        );
                    } else {
                        return (
                            <Notification
                                showCloseButton
                                visible={!notificationState.includes(loyalty.loyaltyProgramId)}
                                onCloseClick={() => handleOnClose(loyalty)}
                                key={loyalty.loyaltyProgramId}
                                status={NotificationStatus.success}
                                headline={t('success.headline')}
                                testId={`testIdSuccess${loyalty.loyaltyProgramId}`}
                            >
                                {t('success.body')}
                            </Notification>
                        );
                    }
                } else {
                    return (
                        <Notification
                            showCloseButton
                            visible={notificationError.includes(loyalty.loyaltyProgramId)}
                            onCloseClick={() =>
                                setNotificationError(state => {
                                    return state.filter(e => e !== loyalty.loyaltyProgramId);
                                })
                            }
                            key={`notification-error-${loyalty.loyaltyProgramId}`}
                            status={NotificationStatus.error}
                            headline={t('error.headline')}
                            testId={`testIdError${loyalty.loyaltyProgramId}`}
                        >
                            {t('error.body')}
                        </Notification>
                    );
                }
            })}
        </>
    );
};
